import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import styled from '@emotion/styled';

import { Button, forMobile, IconButton, Link, scrollbarStyles } from '@/design-system';

import { pipelineColors } from '../../constants';
import { PipelineBadge } from '../../styled';

export const DetailBackdrop = styled(BaseDialog.Backdrop)(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  display: 'block',
  background: theme.colors.black[80],
  zIndex: 1300,
}));

export const DetailPopup = styled(BaseDialog.Popup)(({ theme }) => ({
  position: 'fixed',
  top: 16,
  right: 16,
  bottom: 16,
  width: 400,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: 16,
  background: pipelineColors.surfaceAlt,
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: 12,
  color: pipelineColors.white,
  zIndex: 1301,
  boxSizing: 'border-box',
  outline: 'none',

  [forMobile]: {
    top: 'auto',
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: 'auto',
    maxHeight: 'calc(100dvh - 64px)',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: 'none',
    borderRadius: '20px 20px 0 0',
  },
}));

export const DetailContent = styled('div')({
  minHeight: 0,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  gap: 16,
  overflowY: 'auto',
  ...scrollbarStyles,
});

export const DetailBody = styled('div')({
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  paddingRight: 12,
  overflowY: 'auto',
  boxSizing: 'border-box',
  ...scrollbarStyles,
});

export const DetailDivider = styled('div')(({ theme }) => ({
  height: 1,
  background: theme.colors.border.default,
}));

export const DetailHeader = styled('header')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  minWidth: 0,

  [forMobile]: {
    flexWrap: 'wrap',
  },
});

export const HeaderTitle = styled('span')({
  display: 'block',
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
  color: pipelineColors.white,
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 18,
  fontWeight: 600,
  lineHeight: '18.9px',
  letterSpacing: 0,

  [forMobile]: {
    flex: '1 1 0',
    fontSize: 18,
    lineHeight: '18.9px',
  },
});

export const HeaderTitleLink = styled(Link)(({ theme }) => ({
  display: 'inline',
  maxWidth: '100%',
  padding: 0,
  borderRadius: 2,
  color: pipelineColors.white,
  font: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',

  '&:hover, &:active, &:focus-visible': {
    color: theme.colors.text.brand,
    textDecoration: 'none',
  },
}));

export const DesktopHeaderActions = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,

  [forMobile]: {
    width: '100%',
    paddingLeft: 36,
    boxSizing: 'border-box',
  },
});

export const HeaderContent = styled('div')({
  minWidth: 0,
  flex: '0 0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

export const HeaderLine = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
});

export const HeaderMeta = styled('span')(({ theme }) => ({
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: theme.colors.white[60],
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '18.2px',
  letterSpacing: 0,
}));

export const HeaderMetaLink = styled(Link)(({ theme }) => ({
  display: 'inline',
  maxWidth: '100%',
  padding: 0,
  borderRadius: 2,
  color: theme.colors.text.brand,
  font: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',

  '&:hover, &:active, &:focus-visible': {
    color: theme.colors.text.brand,
    textDecoration: 'none',
  },
}));

export const DetailPillButton = styled(Button)(({ theme }) => ({
  flexShrink: 0,
  padding: '2px 8px',
  minHeight: 21,
  borderRadius: 999,
  background: pipelineColors.badgeSurface,
  border: `1px solid ${theme.colors.border.default}`,
  color: pipelineColors.white,
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '16.8px',
  letterSpacing: 0,
}));

export const HeaderTimerBadge = styled(PipelineBadge)(({ theme }) => ({
  minHeight: 21,
  height: 21,
  borderRadius: 999,
  background: theme.colors.brand.solid,
  color: theme.colors.white[100],

  '&[data-tone="amber"]': {
    background: pipelineColors.yellow,
    color: theme.colors.black[100],
  },

  '&[data-tone="yellow"]': {
    background: pipelineColors.yellow,
    color: theme.colors.black[100],
  },

  '&[data-tone="red"]': {
    background: theme.colors.error.solid,
    color: theme.colors.white[100],
  },
}));

export const DetailBadgeRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
});

export const DetailTypeBadge = styled(PipelineBadge)({
  borderRadius: 8,
});

export const MobileCloseButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  width: 28,
  height: 28,
  minWidth: 28,
  padding: 0,
  borderRadius: 6,
  background: theme.colors.white[4],
  color: theme.colors.white[100],

  svg: {
    width: 18,
    height: 18,
  },

  [forMobile]: {
    display: 'inline-flex',
    flexShrink: 0,
  },
}));

export const DetailBlock = styled('section')({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

export const DetailSection = styled('section')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

export const DetailRow = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr)',
  alignItems: 'flex-start',
  gap: 12,
  minWidth: 0,
});

export const DetailLabel = styled('span')(({ theme }) => ({
  color: theme.colors.white[60],
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '16.8px',
  letterSpacing: 0,
}));

export const DetailValue = styled('span')(({ theme }) => ({
  color: theme.colors.white[100],
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '19.6px',
  letterSpacing: 0,
  textAlign: 'right',
  overflowWrap: 'anywhere',

  '&[data-tone="amber"]': {
    color: pipelineColors.lightYellow,
  },

  '&[data-tone="green"]': {
    color: theme.colors.success.border,
  },

  '&[data-tone="red"]': {
    color: theme.colors.text.error,
  },
}));

export const DetailRowLabel = styled(DetailLabel)({
  textTransform: 'none',
});

export const DetailRowValue = styled('span')(({ theme }) => ({
  minWidth: 0,
  maxWidth: '100%',
  color: theme.colors.white[80],
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '14.4px',
  letterSpacing: 0,
  textAlign: 'right',
  overflowWrap: 'anywhere',

  '&[data-tone="amber"]': {
    color: pipelineColors.lightYellow,
  },

  '&[data-tone="green"]': {
    color: theme.colors.success.border,
  },

  '&[data-tone="red"]': {
    color: theme.colors.text.error,
  },
}));

export const DetailValueLink = styled(Link)(({ theme }) => ({
  display: 'inline',
  padding: 0,
  borderRadius: 2,
  color: theme.colors.text.brand,
  fontSize: 12,
  lineHeight: '14.4px',
  justifyContent: 'flex-end',
  maxWidth: '100%',
  textAlign: 'right',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',

  '&:hover, &:active, &:focus-visible': {
    color: theme.colors.text.brand,
    textDecoration: 'none',
  },
}));

export const DetailState = styled('div')({
  position: 'relative',
  display: 'flex',
  minHeight: 120,
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: 20,
});

export const StateCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  left: 8,
  width: 28,
  height: 28,
  minWidth: 28,
  padding: 0,
  borderRadius: 6,
  background: theme.colors.white[4],
  color: theme.colors.white[100],

  svg: {
    width: 18,
    height: 18,
  },
}));

export const DetailFooter = styled('footer')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'stretch',
  gap: 8,
  padding: '16px 0 0',
  marginTop: 'auto',
  borderTop: `1px solid ${theme.colors.border.default}`,
}));

export const DetailFooterButton = styled(Button)({
  flex: '1 1 0',
  minHeight: 36,
  padding: '7.5px 16px',
  borderRadius: 8,
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '16.1px',
  letterSpacing: 0,
});

export const DetailTimelineList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

export const DetailTimelineItem = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '8px 1fr',
  columnGap: 8,
  alignItems: 'flex-start',

  '&::before': {
    content: '""',
    width: 8,
    height: 8,
    marginTop: 5,
    borderRadius: 999,
    background: theme.colors.white[20],
  },

  '&[data-highlighted="true"]::before': {
    background: theme.colors.brand.solid,
  },
}));

export const DetailTimelineText = styled('div')({
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

export const DetailTimelineTitle = styled('span')(({ theme }) => ({
  color: theme.colors.white[100],
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 13,
  fontWeight: 500,
  lineHeight: '18.2px',
  letterSpacing: 0,
}));

export const DetailTimelineMeta = styled('span')(({ theme }) => ({
  color: theme.colors.white[60],
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '16.8px',
  letterSpacing: 0,
}));
